import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  FormGroup,
  Button,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import PaymentLogServices from "../../Services/QRCodeServices/PaymentLogServices/PaymentLogServices";
import DeleteModal from "../../Components/Common/DeleteModal";
import PaymentAddEditModal from "./Components/PaymentAddEditModal";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { formatDate } from "../../Components/FormatDate/FormatDate";
import NumberCommas from "../../Components/NumberCommas/NumberCommas";
import { Link } from "react-router-dom";
import { exportToExcel } from "../../utils/exportToExcel";

function PaymentLog() {
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [filteredPaymentLogs, setFilteredPaymentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedPaymentLog, setSelectedPaymentLog] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  const toggleModal = () => setModal(!modal);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "80px",
    },
    {
      name: "Supplier Name",
      selector: (row) => row.supplierName,
      sortable: true,
    },
    {
      name: "Invoice ID",
      // selector: (row) =>  row.invoiceId,
      selector: (row) => <Link to={`/invoice/${row.id}`}>{row.invoiceId}</Link>,
      sortable: true,
    },

    {
      name: "Payment Date",
      selector: (row) => formatDate(row.paymentDate),
      sortable: true,
    },
    {
      name: "Payment Method",
      selector: (row) => row.paymentMethod,
      sortable: true,
    },
    {
      name: "Paid Amount",
      selector: (row) => <NumberCommas number={row.paidAmount} />,
      sortable: true,
      right: true,
      width: "150px",
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div className="hstack gap-2">
          <Button
            color="btn btn-soft-warning"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <i className="ri-edit-line"></i>
          </Button>
          <Button
            color="btn btn-soft-danger"
            size="sm"
            onClick={() => handleDeleteClick(row)}
          >
            <i className="ri-delete-bin-line"></i>
          </Button>
        </div>
      ),
    },
  ];

  async function fetchPaymentLogs() {
    try {
      setLoading(true);
      const fetchedPaymentLogs = await PaymentLogServices.getList();
      setPaymentLogs(fetchedPaymentLogs);
      setFilteredPaymentLogs(fetchedPaymentLogs);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching payment logs:", err);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPaymentLogs();
  }, []);

  useEffect(() => {
    let filtered = paymentLogs;

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((log) =>
        log.supplierName?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    setFilteredPaymentLogs(filtered);
  }, [searchTerm, paymentLogs]);

  const handleEdit = (row) => {
    setIsEdit(true);
    setSelectedPaymentLog(row);
    toggleModal();
  };

  const handleCreate = () => {
    setIsEdit(false);
    setSelectedPaymentLog(null);
    toggleModal();
  };

  const handleModalSuccess = () => {
    fetchPaymentLogs();
    toggleModal();
  };

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await PaymentLogServices.delete(selectedDeleteItem.id);
        toast.success("Payment log deleted successfully");
        fetchPaymentLogs();
      } catch (error) {
        toast.error("Failed to delete payment log");
      } finally {
        toggleDeleteModal();
        setSelectedDeleteItem(null);
      }
    }
  };
  const handleExportExcel = () => {
    const columnMapping = {
      "S.N": (row, index) => index + 1,
      "Supplier Name": "supplierName",
      "Invoice ID.": "invoiceId",
      "Payment Date": (row) => formatDate(row.paymentDate),
      "Payment Method": "paymentMethod",
      "Paid Amount": "paidAmount",
    };

    exportToExcel(filteredPaymentLogs, columnMapping, "SupplierPaymentLogs.xlsx");
  };
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Payment Log" pageTitle="Payments" />
          <Row>
            <Col>
              <Card>
                <CardHeader>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <FormGroup className="col-lg-3">
                      <Input
                        type="text"
                        placeholder="Search by Supplier Name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </FormGroup>
                    <div>
                      <button
                        className="btn btn-success"
                        onClick={handleExportExcel}
                      >
                        Export Excel
                      </button>{" "}
                    </div>
                    <div className="ms-auto">
                      <Button color="primary" onClick={handleCreate}>
                        + Create Payment Log
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={filteredPaymentLogs}
                    customStyles={customStyles}
                    responsive
                    striped
                    highlightOnHover
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    progressPending={loading}
                    progressComponent={
                      <div className="my-3">
                        <Triangle
                          visible={true}
                          height="80"
                          width="80"
                          color="#5B71B9"
                          ariaLabel="triangle-loading"
                        />
                        <h5 className="mt-1">Loading...</h5>
                      </div>
                    }
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <PaymentAddEditModal
        isOpen={modal}
        toggle={toggleModal}
        isEdit={isEdit}
        selectedPaymentLog={selectedPaymentLog}
        onSuccess={handleModalSuccess}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={toggleDeleteModal}
        selectedItem={selectedDeleteItem}
      />
    </React.Fragment>
  );
}

export default PaymentLog;
